import { callWithNuxt } from '#app'
import * as expirations from '@packages/cache/expirations'

import { getRuntimeCache, getRuntimeCacheTag, keepAliveAfterResponse } from '@layers/web/utils/runtime-cache.ts'
import type {
  CachedError,
  StaleWhileRevalidateCacheEntry,
  StaleWhileRevalidateOptions,
} from '@layers/web/utils/types/server-cache.ts'

/* Serving a stale entry while it revalidates only works while it is still in the cache, so entries outlive their freshness. */
const STALE_SERVE_DURATION = expirations.dayAndNight

const SERVER_ERROR_STATUS_CODE = 500

/**
 * Promises and errors can't be shared through the runtime cache, so the requests this
 * instance already has in flight are tracked here instead. Other instances filling the
 * same key at the same time is the same duplicated work as before, just as harmless.
 */
const initialRefreshes = new Map<string, Promise<any>>()
const refreshingKeys = new Set<string>()

const toTtlSeconds = (durationInMs: number): number => Math.ceil(durationInMs / 1000)

const toCachedError = (error: any): CachedError => ({
  message: String(error?.message || error),
  statusCode: error?.statusCode,
})

const fromCachedError = ({ message, statusCode }: CachedError): Error => {
  return Object.assign(new Error(message), { statusCode })
}

/**
 * Some important things to consider when using this function:
 * - The cacheKey HAS to be unique, always add any id or params to the key
 * - The refreshFunction HAS to throw an error if the data is wrong, otherwise the wrong data will be cached
 */
const serverCacheStaleWhileRevalidate = async <ReturnType>(
  cacheKey: string,
  refreshFunction: () => Promise<ReturnType>,
  {
    duration = expirations.mediumDuration,
    ignoreCache,
    retryDelayMs,
  }: StaleWhileRevalidateOptions = {}
): Promise<ReturnType> => {
  if (import.meta.client || ignoreCache) {
    return refreshFunction()
  }

  const { public: { previewMode } } = useRuntimeConfig()

  if (previewMode || process.env.NODE_ENV === 'development') {
    return refreshFunction()
  }

  /* All read before the first await: after one the Nuxt instance, and with it every composable, is gone. */
  const nuxtApp = useNuxtApp()
  const cacheTag = getRuntimeCacheTag()
  const cache = await getRuntimeCache()

  const refresh = async (): Promise<ReturnType> => await callWithNuxt(nuxtApp, refreshFunction)

  const getCached = async (): Promise<StaleWhileRevalidateCacheEntry<ReturnType> | null> => {
    return await cache.get(cacheKey) as StaleWhileRevalidateCacheEntry<ReturnType> | null
  }

  const setCached = async (
    cacheEntry: StaleWhileRevalidateCacheEntry<ReturnType>,
    ttlInMs: number
  ): Promise<void> => {
    await cache.set(cacheKey, cacheEntry, {
      ttl: toTtlSeconds(ttlInMs),
      tags: [cacheTag],
    })
  }

  const isThereMoreRecentCache = async (previousExpire?: number): Promise<boolean> => {
    const mostRecentCache = await getCached()

    return (
      !mostRecentCache ||
      !mostRecentCache.expire ||
      mostRecentCache.expire > (previousExpire || 0)
    )
  }

  const cacheRefreshedData = async (data: ReturnType): Promise<void> => {
    await setCached({ data, expire: Date.now() + duration }, duration + STALE_SERVE_DURATION)
  }

  const runInitialRefresh = async (): Promise<ReturnType> => {
    try {
      const data = await refresh()

      await cacheRefreshedData(data)

      return data
    } catch (error) {
      if (retryDelayMs) {
        await setCached(
          { error: toCachedError(error), expire: Date.now() + retryDelayMs },
          retryDelayMs + STALE_SERVE_DURATION
        )
      }

      if (!retryDelayMs) {
        await cache.delete(cacheKey)
      }

      throw error
    } finally {
      initialRefreshes.delete(cacheKey)
    }
  }

  const handleFailedRevalidation = async (
    error: any,
    staleEntry: StaleWhileRevalidateCacheEntry<ReturnType>
  ): Promise<void> => {
    if (await isThereMoreRecentCache(staleEntry.expire)) {
      return
    }

    if (error?.statusCode && error.statusCode < SERVER_ERROR_STATUS_CODE) {
      await cache.delete(cacheKey)
      return
    }

    const expire = retryDelayMs ? Date.now() + retryDelayMs : staleEntry.expire

    await setCached({ ...staleEntry, expire }, duration + STALE_SERVE_DURATION)
  }

  const revalidateInBackground = async (staleEntry: StaleWhileRevalidateCacheEntry<ReturnType>): Promise<void> => {
    try {
      const refreshedData = await refresh()

      if (await isThereMoreRecentCache(staleEntry.expire)) {
        return
      }

      await cacheRefreshedData(refreshedData)
    } catch (error) {
      await handleFailedRevalidation(error, staleEntry)
    } finally {
      refreshingKeys.delete(cacheKey)
    }
  }

  const cached = await getCached()

  if (!cached) {
    const initialRefresh = initialRefreshes.get(cacheKey) || runInitialRefresh()

    initialRefreshes.set(cacheKey, initialRefresh)

    return initialRefresh
  }

  const isStale = !!cached.expire && cached.expire < Date.now()

  if (isStale && !refreshingKeys.has(cacheKey)) {
    refreshingKeys.add(cacheKey)

    keepAliveAfterResponse(revalidateInBackground(cached))
  }

  if (cached.error) {
    throw fromCachedError(cached.error)
  }

  return cached.data as ReturnType
}

export {
  serverCacheStaleWhileRevalidate,
}
